import {
  css,
  mq,
  spacing,
  styled,
  Text,
  zIndex,
  ZIndexLevel,
} from '@thg-commerce/enterprise-theme'
import { AccordionContainer } from '@thg-commerce/enterprise-widget-accordion-container'
import { Separator } from '@thg-commerce/gravity-elements'
import { GridItem } from '@thg-commerce/gravity-system'
import { TextEntry, TextStyle } from '@thg-commerce/gravity-theme'

export const BackButton = styled.button`
  ${Text('bodyText', 'default')};
  display: flex;
  align-items: center;
  padding: ${spacing(2.25)} ${spacing(2)};
  background: ${(props) => props.theme.colors.palette.greys.white};
  margin-bottom: ${spacing(2)};

  svg {
    margin-right: ${spacing(0.75)};
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    display: none;
  }
`

export const Title = styled.h1<{
  textStyling: {
    entry: TextEntry
    style: TextStyle
  }
}>`
  ${(props) =>
    Text(
      props.textStyling?.entry || 'xlarge1',
      props.textStyling?.style || 'alternate',
    )};
  margin-bottom: ${spacing(1)};
  text-align: center;
  letter-spacing: -0.3px;

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    letter-spacing: -0.5px;
  }
`

export const Subtitle = styled.p`
  ${Text('bodyText', 'alternate')};
  text-align: center;
  letter-spacing: 0.2px;
  margin-bottom: ${spacing(2)};
`

export const TextContainer = styled.div`
  margin-bottom: ${spacing(3)};
  background: ${(props) => props.theme.colors.palette.greys.white};
  padding: ${spacing(3)} ${spacing(2)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    padding: ${spacing(4)} ${spacing(4)} ${spacing(5)};
    margin-bottom: ${spacing(4)};
  }
`

export const LeftRailTitle = styled.h2<{
  textStyling: {
    entry: TextEntry
    style: TextStyle
  }
}>`
  ${(props) =>
    Text(
      props.textStyling?.entry || 'large1',
      props.textStyling?.style || 'alternate',
    )};
  letter-spacing: -0.1px;
  padding: 0 ${spacing(2)} ${spacing(2)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    padding: 0 ${spacing(4)} ${spacing(2)};
    letter-spacing: -0.5px;
  }
`

export const StyledGridItem = styled(GridItem)<{
  leftGrid?: boolean
  activeSearch?: boolean
}>`
  ${(props) => (props.activeSearch ? `height: auto;` : `height: fit-content;`)}

  ${(props) =>
    props.leftGrid
      ? css`
          padding: ${spacing(4)} 0;
          background: ${props.theme.colors.palette.greys.white};
        `
      : `padding: 0; background: none;`}

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    background: ${(props) => props.theme.colors.palette.greys.white};
  }
`

export const StyledAccordionContainer = styled.div<{
  openedContent: boolean
}>`
  ${zIndex(ZIndexLevel.Highest)};
  display: flex;
  flex-direction: column;
  position: fixed;
  width: 100%;
  height: 100%;
  padding: 0;
  background: ${(props) => props.theme.colors.palette.greys.lighter};
  top: 0;
  left: 100%;
  transition: left 0.5s ease, right 0.5s ease;
  overflow-y: auto;

  ${(props) =>
    props.openedContent &&
    css`
      left: 0;
      right: 0;
    `};

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    display: block;
    position: relative;
    left: auto;
    top: auto;
    padding: ${spacing(4)};
    ${zIndex(ZIndexLevel.Base)};
    background: ${(props) => props.theme.colors.palette.greys.white};
  }
`

export const StyledAccordionContent = styled(AccordionContainer)<{
  titleStyling?: {
    entry: TextEntry
    style: TextStyle
  }
}>`
  margin: 0 0 ${spacing(4)};
  padding: ${spacing(3)} ${spacing(2)};
  background: ${(props) => props.theme.colors.palette.greys.white};

  h1 {
    ${(props) =>
      props.titleStyling &&
      Text(
        props.titleStyling.entry || 'large1',
        props.titleStyling.style || 'alternate',
      )}
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    padding: 0;
    margin: 0;
  }
`
export const StyledAccordionSearchContainer = styled.div`
  display: none;
  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    display: block;
  }
`

export const StyledAccordionSearchContent = styled(StyledAccordionContent)`
  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    margin: 0 0 ${spacing(4)};

    &:last-child {
      margin: 0;
    }
  }
`

export const StyledMobileAccordionSearchContainer = styled.div`
  display: block;
  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    display: none;
  }
`

export const StyledMobileAccordionSearchContent = styled(AccordionContainer)`
  margin: ${spacing(4)} 0;
`

export const StyledLi = styled.li`
  margin-top: 0;
`

const activeLiStyle = css`
  background-color: ${(props) => props.theme.colors.palette.brand.lightest};
  &:before {
    content: '';
    width: 4px;
    height: 100%;
    display: inline-block;
    left: 0;
    top: 0;
    position: absolute;
    background-color: ${(props) => props.theme.colors.palette.brand.base};
  }
`

export const StyledButton = styled.button<{ active?: boolean }>`
  position: relative;
  transition: background-color 0.3s ease;
  ${Text('bodyText', 'default')};
  padding: ${spacing(1.5)} ${spacing(2)};
  width: 100%;
  text-align: left;

  &:hover {
    background-color: ${(props) => props.theme.colors.palette.brand.lightest};
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    padding: ${spacing(1.5)} ${spacing(4)};
    ${(props) => props.active && activeLiStyle}
  }
`

export const StyledSeparator = styled(Separator)`
  margin: ${spacing(2.5)} ${spacing(2)} ${spacing(4)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    margin: ${spacing(2.5)} ${spacing(4)} ${spacing(4)};
  }
`

export const StyledLiWrapper = styled.li`
  padding: 0 ${spacing(2)};
  margin-top: 0;

  &:hover {
    background-color: ${(props) => props.theme.colors.palette.brand.lightest};
  }

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    padding: 0 ${spacing(4)};
  }
`

const centerStyles = css`
  display: flex;
  align-items: center;
`

export const StyledLink = styled.a`
  ${centerStyles}
  text-decoration: none;
`

export const StyledIcon = styled.span`
  ${centerStyles}
  justify-content: center;
  margin-right: ${spacing(2)};

  svg {
    fill: ${(props) => props.theme.colors.palette.greys.darker};
    width: ${spacing(3)};
    height: ${spacing(3)};
  }
`

export const StyledLinkContent = styled.span`
  ${centerStyles}
  flex-grow: 1;
  flex-wrap: wrap;
  padding: ${spacing(1.5)} 0;
`
export const StyledAltText = styled.span`
  ${Text('xsmall', 'default')};
`

export const AccessibilityBackButtonWrapper = styled.div<{
  showBackButton?: boolean
}>`
  display: none;
  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    ${(props) =>
      props.showBackButton &&
      css`
        display: block;
        position: absolute;
        right: ${spacing(4)};
        top: ${spacing(4)};
      `}
  }
`

export const StyledSearchInputContainer = styled.div`
  width: 100%;

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    width: 60%;
    margin: 0 auto;
  }
`

export const StyledNotFoundText = styled.div`
  display: none;
  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    display: block;
  }
`

export const StyledMobileNotFoundText = styled(StyledNotFoundText)`
  display: block;
  margin-top: ${spacing(2)}
    ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    display: none;
  }
`

export const LiveChatWrapper = styled.div`
  display: none;
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);

  .LPMcontainer.LPMoverlay {
    display: none !important;
  }
`
